<template>
    <Combobox as="div" :disabled="disabled" v-model="state.selected_organisation" @update:modelValue="state.search_string = ''">
        <ComboboxLabel class="block text-sm font-medium leading-6 text-gray-900">Use an existing business?</ComboboxLabel>
        <div class="relative mt-1">
            <ComboboxInput :disabled="disabled" class="w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6" v-bind:class="{'bg-white': !disabled, 'bg-gray-100 cursor-not-allowed': disabled}" @change="state.search_string = $event.target.value" @blur="state.search_string = ''" :display-value="(organisation) => organisation?.name" placeholder="Existing business name" />
            <ComboboxButton v-if="filtered_organisations.length > 0" class="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
                <svg class="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fill-rule="evenodd" d="M10 3a.75.75 0 01.55.24l3.25 3.5a.75.75 0 11-1.1 1.02L10 4.852 7.3 7.76a.75.75 0 01-1.1-1.02l3.25-3.5A.75.75 0 0110 3zm-3.76 9.2a.75.75 0 011.06.04l2.7 2.908 2.7-2.908a.75.75 0 111.1 1.02l-3.25 3.5a.75.75 0 01-1.1 0l-3.25-3.5a.75.75 0 01.04-1.06z" clip-rule="evenodd" />
                </svg>
            </ComboboxButton>

            <ComboboxOptions v-if="filtered_organisations.length > 0" class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                <ComboboxOption v-for="organisation in filtered_organisations" :key="organisation.id" :value="organisation" as="template" v-slot="{ active, selected }">
                    <li :class="['relative cursor-default select-none py-2 pl-3 pr-9 hover:cursor-pointer', active ? 'bg-blue-600 text-white' : 'text-gray-900']">
                        <span :class="['block truncate', selected && 'font-semibold', {'text-blue-600 font-medium': !active && organisation.id === 'CREATE', 'text-white font-medium': !active && organisation.id === 'CREATE'}]">
                            {{ organisation.name }}
                        </span>
                        <span v-if="selected" :class="['absolute inset-y-0 right-0 flex items-center pr-4', active ? 'text-white' : 'text-blue-600']">
                            <CheckIcon class="h-4 w-4" />
                        </span>
                    </li>
                </ComboboxOption>
            </ComboboxOptions>

            <button v-if="state.search_string.length > 0 || state.selected_organisation !== null" v-on:click="() => {state.selected_organisation = null; state.search_string = ''}" class="absolute z-20 right-5 top-0 mt-[-1px] w-[38px] h-[38px] flex items-center justify-center text-gray-500"><CrossCircleIcon class="h-5 w-5" /><span class="sr-only">Clear field contents</span></button>
        </div>
    </Combobox>

    <Message :active="state.results_message_active && state.search_string.length > 0 && filtered_organisations.length === 0" heading="No organisations match your search" type="error" class="mt-4" />
</template>

<script>
import { reactive, computed, watch, onMounted, ref } from "vue";
import { useAuctioneerStore } from "@sto/auctioneer.store"

import CheckIcon from "@ico/CheckIcon.vue"
import CrossCircleIcon from "@ico/CrossCircleIcon.vue"
import {
    Combobox,
    ComboboxButton,
    ComboboxInput,
    ComboboxLabel,
    ComboboxOption,
    ComboboxOptions,
} from '@headlessui/vue'

export default {
    components: {
        Combobox,
        ComboboxButton,
        ComboboxInput,
        ComboboxLabel,
        ComboboxOption,
        ComboboxOptions,
        CheckIcon,
        CrossCircleIcon
    },
    props: ['auctioneer_id', 'disabled'],
    setup(props, { emit }) {

        const AUCTIONEER_STORE = useAuctioneerStore()

        const state = reactive({
            organisations_loading: false,
            organisation_options: [],
            selected_organisation: null,
            organisation_timeout: null,
            search_string: '',
            results_message_active: false
        })

        /**
         * get all the existing organisations that are currently associated to this auctioneer
         * @returns {Promise<void>}
         */
        const getAuctioneerAssociatedOrganisations = async () => {

            if (state.organisations_loading) return

            state.organisations_loading = true

            try {

                const organisations_data = await AUCTIONEER_STORE.getAssociatedOrganisations(props.auctioneer_id, [
                    { key: 'per_page', value: 100 },
                    { key: 'page', value: 1 },
                    { key: 'search_string', value: state.search_string }
                ])

                state.organisation_options = organisations_data.organisations.map(({id, name}) => {
                    return {
                        id,
                        name
                    }
                })


            } catch (error) {
                console.error('list organisations error', error)

            } finally {

                state.organisations_loading = false
            }
        }

        const filtered_organisations = computed(() => {
            return state.search_string === ''
                ? state.organisation_options
                : state.organisation_options.filter((org) => {
                    return org.name.toLowerCase().includes(state.search_string.toLowerCase())
                })
        })

        watch(
            () => state.search_string,
            (new_value) => {
                state.results_message_active = false
                if (state.organisation_timeout) clearTimeout(state.organisation_timeout)
                state.organisation_timeout = setTimeout(getAuctioneerAssociatedOrganisations, 500)
            }
        )

        watch(
            () => state.selected_organisation,
            (new_value) => {
                emit('value_update', state.selected_organisation || null)
            }
        )

        onMounted(() => {
            getAuctioneerAssociatedOrganisations()
        })

        return {
            state,
            filtered_organisations
        }
    }
}
</script>